/**
 * PreDrawFormBridge — Connects Alurack's existing <AddSegmentFormContainer />
 * Redux flow to the pv-layout-core widget.
 *
 * Background
 * ----------
 * The widget exposes `onSegmentDrawRequest({ drawingType, startDraw })` so the
 * host app can display a pre-draw form and then hand the collected settings
 * back through `startDraw({ initialSettings, drawingMode })`.
 *
 * Alurack already has an `AddSegmentFormContainer` dialog whose submit handler
 * dispatches three Redux actions in sequence:
 *   1. setNewSegmentConfig(values)            → stores the submitted config
 *   2. setAddSegmentModalOpenStatus(false)    → closes the dialog
 *   3. setDrawingMode(true, drawingMode)      → flips draw mode on
 *
 * Rather than rewriting that form, this bridge tracks per-cycle transitions
 * on the `isModalOpen` flag and on the `newSegmentConfig` object reference:
 *
 *   - A "cycle" starts when the modal transitions closed → open.
 *   - While a cycle is active, a change in `newSegmentConfig` reference
 *     (Redux always dispatches a fresh object on submit) is interpreted as
 *     submission and forwards the payload through `onSubmit`.
 *   - While a cycle is active, a modal close without a config reference
 *     change is interpreted as cancel and forwards through `onCancel`.
 *
 * This approach is immune to stale Redux state between segments (the widget's
 * V2 flow doesn't reset `isDrawingMode` / `newSegmentConfig` between draws)
 * because detection hinges on in-cycle transitions, not on absolute values.
 *
 * The component renders nothing — it is purely a Redux ↔ widget relay.
 */

import { useEffect, useRef } from 'react';

import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import * as selectors from '../selectors';

function PreDrawFormBridge({
    drawingType,
    isModalOpen,
    newSegmentConfig,
    onCancel,
    onSubmit,
}) {
    const prevModalOpenRef = useRef(Boolean(isModalOpen));
    const prevConfigRef = useRef(newSegmentConfig);
    const cycleActiveRef = useRef(false);

    useEffect(() => {
        const wasOpen = prevModalOpenRef.current;
        const prevConfig = prevConfigRef.current;
        const hasConfig = !isEmpty(newSegmentConfig);
        const configChanged = newSegmentConfig !== prevConfig;

        // Closed → open: start a new cycle.
        if (isModalOpen && !wasOpen) {
            cycleActiveRef.current = true;
        }

        if (cycleActiveRef.current) {
            if (configChanged && hasConfig) {
                // Submit: Redux dispatched a new config object.
                onSubmit?.({
                    newSegmentConfig,
                    drawingMode: drawingType,
                });
                cycleActiveRef.current = false;
            } else if (wasOpen && !isModalOpen) {
                // Open → closed without a config change: cancel.
                onCancel?.();
                cycleActiveRef.current = false;
            }
        }

        prevModalOpenRef.current = Boolean(isModalOpen);
        prevConfigRef.current = newSegmentConfig;
    }, [drawingType, isModalOpen, newSegmentConfig, onCancel, onSubmit]);

    return null;
}

PreDrawFormBridge.propTypes = {
    drawingType: PropTypes.number,
    isModalOpen: PropTypes.bool,
    newSegmentConfig: PropTypes.object,
    onCancel: PropTypes.func,
    onSubmit: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
    drawingType: selectors.getDrawingType,
    isModalOpen: selectors.getAddSegmentModalOpenStatus,
    newSegmentConfig: selectors.getNewSegmentConfig,
});

export default connect(mapStateToProps)(PreDrawFormBridge);
